import type { PageAdConfig } from 'interfaces/ads/Ad';
import type CustomWindow from 'interfaces/Utils';
import Script from 'next/script';
import { UserContext } from 'providers/UserProvider';
import type React from 'react';
import { useContext, useEffect, useState } from 'react';
import { useAdContext } from 'services/Ad';
import type Nativo from 'services/Nativo/entities';
import type { NativoCmd } from 'services/Nativo/entities';

interface AdManagerProps {
  pageAdConfig: PageAdConfig;
  setAdContextValue: (value: any) => void;
}

declare let window: CustomWindow;

const AdManager: React.FC<AdManagerProps> = ({ pageAdConfig, setAdContextValue }) => {
  const [isArctagReady, setIsArctagReady] = useState(false);
  const [shouldStartNativo, setShouldStartNativo] = useState(false);
  const [isNativoStarted, setIsNativoStarted] = useState(false);
  const { personalizedAds } = useContext(UserContext);
  const adContextValue = useAdContext(pageAdConfig);
  setAdContextValue(adContextValue);

  const CLPcheck =
    typeof window !== 'undefined' &&
    (window.location.search.includes('prx_t') || window.location.search.includes('ntv_adpz'));

  const noNativoTags = ['Personal Finance Evergreen', 'Health Evergreen', 'no-nativo'];

  const noNativoAds =
    noNativoTags.some((tag) => pageAdConfig.tags?.includes(tag)) || pageAdConfig.topics?.includes('no-nativo');

  useEffect(() => {
    // avoid race condition and intialize nativo cmd
    if (typeof window !== 'undefined') {
      window.ntv = window.ntv || ({} as NativoCmd);
      window.ntv.cmd = window.ntv.cmd || [];
    }

    // Set Nativo targeting
    const kvpMap: { [key: string]: string } = {};
    kvpMap.topics = pageAdConfig.topics ? pageAdConfig.topics.join(',') : '';
    kvpMap.arcspan = (window?.arcobj2?.activation?.['Google Ad Manager'] || []).join(',');
    window.ntvConfig = window.ntvConfig || ({} as Nativo);
    window.ntvConfig.keyValues = kvpMap;

    if (window.ntvConfig && window.ntvConfig.keyValues && personalizedAds) {
      window.ntvConfig.keyValues.arcspan = (window?.arcobj2?.activation?.['Google Ad Manager'] || []).join(',');
    }

    // Listen for ArctagReady event to know when Arcspan was initialized
    const handleArctagReady = () => {
      setIsArctagReady(true);
    };

    window.addEventListener('arctagReady', handleArctagReady);

    // Attach callbacks to window object
    // The ArcSpan injection script in /Arcspan/index.tsx will call these handlers
    if (typeof window !== 'undefined') {
      window.onArcspanScriptLoad = () => {
        setShouldStartNativo(true);
      };

      window.onArcspanScriptError = () => {
        setShouldStartNativo(true);
      };
    }
    return () => {
      // Clean up the global functions
      if (typeof window !== 'undefined') {
        window.removeEventListener('arctagReady', handleArctagReady);
        delete window.onArcspanScriptLoad;
        delete window.onArcspanScriptError;
      }
    };
  }, [pageAdConfig.topics, personalizedAds]);

  useEffect(() => {
    if (!personalizedAds || noNativoAds) {
      return;
    }

    if (isArctagReady) {
      let arcspanValue = '';

      if (window?.arcobj2?.activation) {
        arcspanValue = window.arcobj2.activation['Google Ad Manager']?.join(',') || '';
      }
      window.ntvConfig = window.ntvConfig || { keyValues: {} };
      window.ntvConfig.keyValues = {
        ...window.ntvConfig.keyValues,
        arcspan: arcspanValue,
      };
    }

    // Initialize Nativo when Arcspan script is ready or loading Arcspan script failed
    if (isArctagReady || shouldStartNativo) {
      setIsNativoStarted(true);
    }
  }, [personalizedAds, noNativoAds, isArctagReady, shouldStartNativo]);

  useEffect(() => {
    if (!isNativoStarted) {
      return;
    }

    window.ntv.cmd.push(() => {
      window.PostRelease?.Start();
    });
  }, [isNativoStarted]);

  return (
    <>
      {!noNativoAds && (
        <Script
          id='nativo-js'
          src='https://s.ntv.io/serve/load.js'
          strategy='afterInteractive'
          data-ntv-set-no-auto-start
          {...(CLPcheck && { 'data-ntv-pub-injection': true })}
        />
      )}

      {CLPcheck && (
        <Script
          id='nativo-iframe'
          src='https://assets.promotedarticle.com/iframeInjection.js'
          onLoad={() => {
            if (window.ntv && window.ntv.cmd) {
              window.ntv.cmd.push(() => {
                window.PostRelease?.Start();
              });
            }
          }}
        />
      )}
    </>
  );
};

export default AdManager;
